import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IndianRupee, Wallet, Filter, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import Cookies from 'js-cookie';
import { canManageLabourWages } from '../../../shared/utils/workforceAccess';
import { newIdempotencyKey } from '../../../shared/utils/idempotency';
import {
  getLabourDues,
  getLabourWageLedger,
  recordLabourPayment,
  recordLabourAdjustment,
  getPieceRates,
  createPieceRate,
  getWorkMeasurements,
  recordWorkMeasurement,
  decideWorkMeasurement
} from '../../services/api/implementation/impl';
import {
  Tile
} from '../../../shared/components/ui/primitives';

const ENTRY_TYPES = [
  'ATTENDANCE',
  'OVERTIME',
  'PIECE_RATE',
  'PAYMENT',
  'ADVANCE',
  'DEDUCTION',
  'FINE',
  'ADJUSTMENT'
];

const PAYMENT_MODES = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE'];

/**
 * Adjustments are split by type rather than offered as one "adjust balance"
 * field, because each lands in a different statutory register — an advance in
 * Form XXII, a fine in Form XXI, a deduction in Form XX. Recording a fine as a
 * generic adjustment would keep the arithmetic right and the register wrong.
 */
const ADJUSTMENT_TYPES = [
  { value: 'ADVANCE', label: 'Advance', hint: 'Money paid ahead of work done' },
  { value: 'DEDUCTION', label: 'Deduction', hint: 'For damage or loss' },
  { value: 'FINE', label: 'Fine', hint: 'Recorded in the register of fines' },
  { value: 'ADJUSTMENT', label: 'Correction', hint: 'Fixes an earlier mistake' }
];

const LabourWages = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const queryClient = useQueryClient();

  // Accounts can open this screen and cannot write to it — the server grants
  // them can_view and nothing else. Drawing Pay and Adjust for them would offer
  // an action that fails, which is worse than not offering it.
  const canRecord = canManageLabourWages(
    Cookies.get('userDesignation'),
    Cookies.get('userVertical')
  );
  const [selected, setSelected] = useState(null);
  const [payModal, setPayModal] = useState(false);
  const [adjustModal, setAdjustModal] = useState(false);

  const { data: dues, isFetching } = useQuery({
    queryKey: ['labourDues'],
    queryFn: () => getLabourDues(200)
  });

  const [filters, setFilters] = useState({ from: '', to: '', entry_type: '' });
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);

  const { data: ledger, isFetching: loadingLedger } = useQuery({
    queryKey: ['labourLedger', selected?.worker_id, filters],
    queryFn: () => getLabourWageLedger(selected.worker_id, filters),
    enabled: Boolean(selected?.worker_id)
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['labourDues'] });
    queryClient.invalidateQueries({ queryKey: ['labourLedger'] });
  };

  const pay = useMutation({
    mutationFn: recordLabourPayment,
    onSuccess: () => {
      refresh();
      setPayModal(false);
      toast.success('Payment recorded');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the payment')
  });

  const adjust = useMutation({
    mutationFn: recordLabourAdjustment,
    onSuccess: () => {
      refresh();
      setAdjustModal(false);
      toast.success('Entry recorded');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the entry')
  });

  const rows = dues?.data ?? [];
  const meta = dues?.meta ?? {};
  const visibleRows = onlyOutstanding
    ? rows.filter((r) => Number(r.due_amount) > 0)
    : rows;

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Labour Wages' }} />

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile variant="card"
            label="Total outstanding"
            value={formatMoney(meta.total_due)}
            icon={<Wallet size={18} />}
          />
          <Tile variant="card"
            label="Earned to date"
            value={formatMoney(meta.total_earned)}
            icon={<IndianRupee size={18} />}
          />
          <Tile variant="card"
            label="Paid to date"
            value={formatMoney(meta.total_paid)}
            icon={<IndianRupee size={18} />}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <span className="font-medium text-gray-800">Outstanding dues</span>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={onlyOutstanding}
                  onChange={(e) => setOnlyOutstanding(e.target.checked)}
                  className="h-3.5 w-3.5 accent-brand-700"
                />
                Only those still owed
              </label>
            </div>
            {!canRecord && (
              <p className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
                Read-only. Payments and adjustments are recorded by the site
                engineer who keeps the muster.
              </p>
            )}
            {isFetching ? (
              <Loader />
            ) : (
              <div className="overflow-x-auto max-h-[560px]">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <Th>Workman</Th>
                      <Th className="text-right">Earned</Th>
                      <Th className="text-right">Paid</Th>
                      <Th className="text-right">Balance</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-gray-500">
                          Nothing outstanding.
                        </td>
                      </tr>
                    )}
                    {visibleRows.map((row) => (
                      <tr
                        key={row.worker_id}
                        className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 ${
                          selected?.worker_id === row.worker_id ? 'bg-brand-50' : ''
                        }`}
                        onClick={() => setSelected(row)}
                      >
                        <Td>
                          <div className="font-medium text-gray-900">{row.name}</div>
                          <div className="text-xs text-gray-500">
                            {row.contractor ?? 'In-house'}
                          </div>
                          {/* `wage_period` used to be printed on Form XIII and
                              read by nothing. Read as the payment cadence, it
                              means this: a weekly workman unpaid for twelve
                              days. */}
                          {row.settlement_overdue && (
                            <div className="mt-0.5 text-xs font-medium text-warning-fg">
                              {titleCase(row.wage_period)} pay ·{' '}
                              {row.days_since_paid} days since last paid
                            </div>
                          )}
                        </Td>
                        <Td className="text-right text-gray-700">
                          {formatMoney(row.earned)}
                        </Td>
                        <Td className="text-right text-gray-500">
                          {formatMoney(row.total_paid)}
                        </Td>
                        <Td className="text-right">
                          <span
                            className={
                              row.due_amount < 0
                                ? 'font-medium text-warning-fg'
                                : 'font-medium text-gray-900'
                            }
                          >
                            {formatMoney(row.due_amount)}
                          </span>
                          {row.due_amount < 0 && (
                            <div className="text-xs text-warning-fg">advance drawn</div>
                          )}
                        </Td>
                        <Td>
                          {canRecord ? (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelected(row);
                                  setPayModal(true);
                                }}
                                className="rounded bg-brand-700 px-2.5 py-1 text-xs text-white"
                              >
                                Pay
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelected(row);
                                  setAdjustModal(true);
                                }}
                                className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700"
                              >
                                Adjust
                              </button>
                            </div>
                          ) : null}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <span className="font-medium text-gray-800">
                {selected ? `Ledger — ${selected.name}` : 'Ledger'}
              </span>
              {selected && (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close ledger"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {!selected ? (
              <div className="py-10 text-center text-sm text-gray-500">
                Select a workman to see every movement against them.
              </div>
            ) : (
              <>
                {/* What is owed right now, across all time — which is what
                    "outstanding" means to the person about to hand over cash.
                    Deliberately not the filtered window's balance. */}
                <div className="grid grid-cols-2 gap-px border-b border-gray-100 bg-gray-100 sm:grid-cols-4">
                  <Stat
                    label="Outstanding"
                    value={formatMoney(ledger?.summary?.outstanding_total)}
                    strong
                  />
                  <Stat label="Earned (Cr)" value={formatMoney(ledger?.summary?.total_credit)} />
                  <Stat
                    label="Paid &amp; deductions (Dr)"
                    value={formatMoney(ledger?.summary?.total_debit)}
                  />
                  <Stat
                    label="Last paid"
                    value={
                      ledger?.summary?.last_paid_on
                        ? `${formatMoney(ledger.summary.last_paid_amount)} · ${formatDate(
                            ledger.summary.last_paid_on
                          )}`
                        : 'Never'
                    }
                  />
                </div>

                <div className="flex flex-wrap items-end gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3">
                  <Filter size={14} className="mb-2 text-gray-400" />
                  <FilterField label="From">
                    <input
                      type="date"
                      value={filters.from}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, from: e.target.value }))
                      }
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                    />
                  </FilterField>
                  <FilterField label="To">
                    <input
                      type="date"
                      value={filters.to}
                      onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                    />
                  </FilterField>
                  <FilterField label="Type">
                    <select
                      value={filters.entry_type}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, entry_type: e.target.value }))
                      }
                      className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                    >
                      <option value="">All</option>
                      {ENTRY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {titleCase(t)}
                        </option>
                      ))}
                    </select>
                  </FilterField>
                  {(filters.from || filters.to || filters.entry_type) && (
                    <button
                      type="button"
                      onClick={() => setFilters({ from: '', to: '', entry_type: '' })}
                      className="mb-0.5 text-xs text-brand-700 hover:underline"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {loadingLedger ? (
                  <Loader />
                ) : (
                  <div className="max-h-[520px] overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-gray-600">
                        <tr>
                          <Th>Date</Th>
                          <Th>Particulars</Th>
                          <Th className="text-right">Earned (Cr)</Th>
                          <Th className="text-right">Paid &amp; ded. (Dr)</Th>
                          <Th className="text-right">Balance</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filters.from && (
                          <tr className="border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
                            <Td colSpan={4}>Balance brought forward</Td>
                            <Td className="text-right font-medium text-gray-700">
                              {formatMoney(ledger?.opening_balance)}
                            </Td>
                          </tr>
                        )}
                        {(ledger?.entries ?? []).length === 0 && (
                          <tr>
                            <td colSpan={5} className="py-10 text-center text-gray-500">
                              Nothing in this range.
                            </td>
                          </tr>
                        )}
                        {(ledger?.entries ?? []).map((entry) => (
                          <tr key={entry.id} className="border-t border-gray-100 align-top">
                            <Td className="whitespace-nowrap">
                              {formatDate(entry.entry_date)}
                            </Td>
                            <Td>
                              <div className="font-medium text-gray-800">
                                {titleCase(entry.entry_type)}
                              </div>
                              {entry.narration && (
                                <div className="text-xs text-gray-500">
                                  {entry.narration}
                                </div>
                              )}
                              {/* The muster behind the money. "Attendance —
                                  Rs 450" is unanswerable on its own; the day,
                                  the hours and the task are what let somebody
                                  agree with it or dispute it. */}
                              {entry.attendance && (
                                <div className="mt-1 text-xs text-gray-500">
                                  {formatDate(entry.attendance.date)} ·{' '}
                                  {entry.attendance.working_hours}h
                                  {entry.attendance.overtime_hours > 0 &&
                                    ` + ${entry.attendance.overtime_hours}h OT`}
                                  {entry.attendance.break_minutes > 0 &&
                                    ` · ${entry.attendance.break_minutes}m break`}
                                  {entry.attendance.project &&
                                    ` · ${entry.attendance.project.name}`}
                                  {entry.attendance.task &&
                                    ` · ${entry.attendance.task.name}`}
                                </div>
                              )}
                              {entry.rate != null && (
                                <div className="text-xs text-gray-400">
                                  at {formatMoney(entry.rate)}
                                  {entry.hours ? ` × ${entry.hours}h` : ''}
                                </div>
                              )}
                            </Td>
                            <Td className="whitespace-nowrap text-right text-success-fg">
                              {entry.credit ? formatMoney(entry.credit) : ''}
                            </Td>
                            <Td className="whitespace-nowrap text-right text-danger-fg">
                              {entry.debit ? formatMoney(entry.debit) : ''}
                            </Td>
                            <Td className="whitespace-nowrap text-right font-medium text-gray-900">
                              {formatMoney(entry.running_balance)}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <PieceRatePanel canRecord={canRecord} defaultWorkerId={selected?.worker_id} />

      {payModal && selected && (
        <PaymentModal
          worker={selected}
          busy={pay.isPending}
          onClose={() => setPayModal(false)}
          onSubmit={(values) => pay.mutate({ worker_id: selected.worker_id, ...values })}
        />
      )}

      {adjustModal && selected && (
        <AdjustmentModal
          worker={selected}
          busy={adjust.isPending}
          onClose={() => setAdjustModal(false)}
          onSubmit={(values) =>
            adjust.mutate({ worker_id: selected.worker_id, ...values })
          }
        />
      )}
    </Layout>
  );
};

const PieceRatePanel = ({ canRecord, defaultWorkerId }) => {
  const queryClient = useQueryClient();
  const [uom, setUom] = useState('sqft');
  const [rate, setRate] = useState('');
  const [workerId, setWorkerId] = useState(defaultWorkerId ? String(defaultWorkerId) : '');
  const [qty, setQty] = useState('');
  const [gangId, setGangId] = useState('');
  const [allocations, setAllocations] = useState('');

  useEffect(() => {
    if (defaultWorkerId) setWorkerId(String(defaultWorkerId));
  }, [defaultWorkerId]);
  const rates = useQuery({ queryKey: ['pieceRates'], queryFn: getPieceRates });
  const pending = useQuery({
    queryKey: ['workMeasurements'],
    queryFn: () => getWorkMeasurements({ status: 'PENDING' })
  });

  const saveRate = useMutation({
    mutationFn: createPieceRate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pieceRates'] });
      toast.success('Rate recorded');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save the rate')
  });
  const measure = useMutation({
    mutationFn: recordWorkMeasurement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workMeasurements'] });
      queryClient.invalidateQueries({ queryKey: ['labourDues'] });
      toast.success('Measurement recorded — approve to post the wage');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not record')
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }) => decideWorkMeasurement(id, { decision }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workMeasurements'] });
      queryClient.invalidateQueries({ queryKey: ['labourDues'] });
      toast.success('Decided');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not decide')
  });

  const rateRows = rates.data?.data ?? [];
  const pendingRows = pending.data?.data ?? [];

  return (
    <div className="mx-4 mb-6 space-y-3 rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-sm font-semibold text-gray-800">Piece-rate / measured work</div>
      <p className="text-xs text-gray-500">
        Attendance stays on the muster. Approving a measurement writes a PIECE_RATE
        credit to the same wage ledger. A group measurement splits quantity equally
        unless you name who did how much.
      </p>
      <div className="flex flex-wrap gap-2 items-end">
        <input
          placeholder="UOM (sqft, cum…)"
          value={uom}
          onChange={(e) => setUom(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-36"
        />
        <input
          type="number"
          placeholder="₹ / unit"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-28"
        />
        {canRecord && (
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          disabled={!uom || !rate}
          onClick={() => saveRate.mutate({ uom, rate: Number(rate) })}
        >
          Save rate
        </button>
        )}
        <span className="text-xs text-gray-400 self-center">
          {rateRows.length} rate{rateRows.length === 1 ? '' : 's'} on file
        </span>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <input
          placeholder="Worker id"
          value={workerId}
          onChange={(e) => setWorkerId(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-28"
        />
        <input
          placeholder="Group id"
          value={gangId}
          onChange={(e) => setGangId(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-28"
        />
        <input
          type="number"
          placeholder="Quantity"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-28"
        />
        {canRecord && (
        <button
          type="button"
          className="rounded bg-brand-700 px-3 py-2 text-sm text-white disabled:opacity-50"
          disabled={(!workerId && !gangId && !allocations.trim()) || (!qty && !allocations.trim())}
          onClick={() =>
            measure.mutate({
              worker_id: workerId ? Number(workerId) : undefined,
              gang_id: gangId ? Number(gangId) : undefined,
              quantity: qty ? Number(qty) : undefined,
              uom,
              rate: rate ? Number(rate) : undefined,
              allocations: parseAllocations(allocations)
            })
          }
        >
          Record measurement
        </button>
        )}
      </div>
      <textarea
        placeholder="Named split (optional) — one per line: workerId quantity"
        value={allocations}
        onChange={(e) => setAllocations(e.target.value)}
        rows={2}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />
      {pendingRows.length > 0 && (
        <ul className="divide-y divide-gray-100 text-sm">
          {pendingRows.map((m) => (
            <li key={m.id} className="flex items-center justify-between py-2">
              <span>
                {m.worker?.name ?? `Worker ${m.worker_id}`} · {m.quantity} {m.uom} · ₹
                {m.amount}
              </span>
              {canRecord && (
              <span className="flex gap-2">
                <button
                  type="button"
                  className="text-brand-700 hover:underline"
                  onClick={() => decide.mutate({ id: m.id, decision: 'APPROVE' })}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="text-danger-fg hover:underline"
                  onClick={() => decide.mutate({ id: m.id, decision: 'REJECT' })}
                >
                  Reject
                </button>
              </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PaymentModal = ({ worker, busy, onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('CASH');
  const [reference, setReference] = useState('');
  const [remarks, setRemarks] = useState('');

  // Generated once when the modal opens, not per submit. That is the whole
  // point: a second tap on the same open form is the SAME payment, and the
  // server returns the one it already recorded rather than disbursing again.
  const [idempotencyKey] = useState(() => newIdempotencyKey('pay'));

  const submit = () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    onSubmit({
      amount: value,
      mode,
      reference,
      remarks,
      client_payment_uuid: idempotencyKey
    });
  };

  return (
    <Modal name={`Record payment — ${worker.name}`} onClose={onClose} width="480px">
      <div className="space-y-3">
        <Field label="Amount">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0.00"
          />
        </Field>
        <Field label="Mode">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {PAYMENT_MODES.map((m) => (
              <option key={m} value={m}>
                {titleCase(m)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reference (UTR / cheque no.)">
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Remarks">
          <input
            type="text"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            {busy ? 'Recording…' : 'Record payment'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const AdjustmentModal = ({ worker, busy, onClose, onSubmit }) => {
  const [type, setType] = useState('ADVANCE');
  const [amount, setAmount] = useState('');
  const [narration, setNarration] = useState('');

  const selectedType = ADJUSTMENT_TYPES.find((t) => t.value === type);

  const submit = () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value === 0) {
      toast.error('Enter a non-zero amount');
      return;
    }
    // The server requires this too. A fine or deduction with no stated reason is
    // precisely what the statutory register exists to prevent.
    if (!narration.trim()) {
      toast.error('A reason is required — it appears in the statutory register');
      return;
    }
    onSubmit({ entry_type: type, amount: value, narration: narration.trim() });
  };

  return (
    <Modal name={`Ledger entry — ${worker.name}`} onClose={onClose} width="480px">
      <div className="space-y-3">
        <Field label="Type">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {ADJUSTMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">{selectedType?.hint}</p>
        </Field>

        <Field label="Amount">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0.00"
          />
          {type !== 'ADJUSTMENT' && (
            <p className="text-xs text-gray-500 mt-1">
              Reduces what is owed to the workman.
            </p>
          )}
        </Field>

        <Field label="Reason">
          <input
            type="text"
            value={narration}
            onChange={(e) => setNarration(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Appears in the statutory register"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            {busy ? 'Recording…' : 'Record entry'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

function formatMoney(value) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return '₹0';
  return `₹${number.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function titleCase(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const Th = ({ children, className = '' }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left font-medium ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = '', colSpan }) => (
  <td className={`px-4 py-3 align-top ${className}`} colSpan={colSpan}>
    {children}
  </td>
);

/**
 * A figure in the ledger header strip.
 *
 * `tabular-nums` because these sit in a row and are compared against each other
 * — proportional digits make two amounts of the same magnitude look different
 * widths, which is exactly when somebody misreads one.
 */
const Stat = ({ label, value, strong = false }) => (
  <div className="bg-white px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div
      className={`mt-0.5 tabular-nums ${
        strong ? 'text-lg font-semibold text-gray-900' : 'text-sm text-gray-700'
      }`}
    >
      {value}
    </div>
  </div>
);

const FilterField = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}</span>
    {children}
  </label>
);

function parseAllocations(text) {
  const rows = String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [id, qty] = line.split(/[\s,=]+/);
      const worker_id = Number(id);
      const quantity = Number(qty);
      if (!(worker_id > 0) || !(quantity > 0)) return null;
      return { worker_id, quantity };
    })
    .filter(Boolean);
  return rows.length ? rows : undefined;
}

export default LabourWages;
